/**
 * The React face of the offline capture queue: what `SyncChip` and `HeldBanner`
 * render from, and the one place the browser's own events are wired to it.
 *
 * The rules live in `queue.ts` and are tested without React. This file only
 * carries three responsibilities the queue deliberately does not take on:
 *
 *   · telling the queue where identity comes from — this file imports MSAL so
 *     that `queue.ts` does not have to, which is what keeps every test of the
 *     attribution rules free of a browser;
 *   · replaying when the network comes back, when the app boots, and when the
 *     signed-in account changes — that last one is how a held capture goes home;
 *   · holding the snapshot in state so the chip re-renders.
 *
 * `held` is a list, not a count, because the two surfaces need different things:
 * the chip shows `held.length`, and the banner has to NAME the account that must
 * sign in to release each item. A refusal nobody can see is a slower silent loss.
 */
import { useCallback, useEffect, useState } from 'react';
import { acquireApiToken, currentUpn } from '../config/auth';
import {
  captureQueue,
  setQueueIdentity,
  type CaptureContext,
  type CaptureDraft,
  type CapturePhoto,
  type CaptureQueue,
  type PhotoRefusal,
  type QueuePhotoRow,
  type HeldItem,
  type QueueIdentity,
  type QueueRefusal,
  type QueueRow,
  type QueueSnapshot,
  type ReplaySummary,
} from './queue';

/**
 * The app's identity, and the DEFAULT — not an opt-in a shell has to remember.
 *
 * `queue.ts` fails closed: with no identity registered, `getUpn()` answers `''`,
 * every capture is stamped unattributable and every item is held for ever. That
 * is the right default for an unwired queue and the wrong state for the app, so
 * the wiring lives here rather than in a shell that can forget it — there is no
 * screen on which the queue should be running without one.
 *
 * Both halves come from `config/auth.ts` so the stamp and the bearer are read
 * from the SAME MSAL account. Splitting them across two sources is how the stamp
 * and the token come to disagree, which is the defect this queue exists to stop.
 *
 * Module-level, so the reference is stable and the effect below runs once.
 */
const msalIdentity: QueueIdentity = {
  getUpn: currentUpn,
  // `acquireApiToken` throws — a failed silent renewal, a non-Majans account, no
  // account at all. The queue's contract here is a token or null, and null means
  // KEEP the item: the same answer a 401 gets, because the capture is real and
  // what is wrong is the session.
  getToken: async () => {
    try {
      return await acquireApiToken();
    } catch {
      return null;
    }
  },
};

export interface UseQueueOptions {
  /** Overrides the app's MSAL identity above. For tests and for a harness; the
   *  app passes nothing. Memoise anything you do pass — it is an effect
   *  dependency, and a fresh object every render re-registers on every render. */
  identity?: QueueIdentity;
  /** The account signed in right now. Changing it re-runs replay, which is what
   *  releases a held capture when its own account returns to this device. */
  signedInUpn?: string;
  /** Injected in tests; the app uses the singleton. */
  queue?: CaptureQueue;
}

export interface UseQueueResult {
  /** Items belonging to the CURRENT account — the chip's "N queued". A
   *  colleague's held orphan is not counted here and must not read as this
   *  operator's backlog. */
  /** Items this account still owes the server, or `null` when the device could
   *  not read its own queue. A caller must never treat null as zero. */
  queued: number | null;
  /** Counted and named separately: a held item is precisely the one that is NOT
   *  on its way, so folding it into `queued` would be a false promise. */
  held: HeldItem[];
  /** The account the banner should name, when every held item belongs to ONE.
   *  Null when they come from several accounts or carry no stamp: naming one of
   *  several would tell the operator that somebody who never keyed those
   *  readings is the person who has to sign in. */
  heldOwner: string | null;
  online: boolean;
  enqueue: (draft: CaptureDraft) => Promise<QueueRow>;
  replay: () => Promise<ReplaySummary>;
  /** The only non-automatic exit for a held capture, and it needs the owner's
   *  UPN. The caller confirms with a human first — there is no "send it as me". */
  discardHeld: (queueId: number, ownerUpn: string) => Promise<void>;
  /** Captures the server REFUSED. Rule 4 says a rejected reading is stored and
   *  never dropped; this is the half of it the operator can see. */
  refusals: QueueRefusal[];
  /** Refusals past the cap, so a banner never implies it is showing them all. */
  refusalsDropped: number;
  /** Dismiss what has been read. */
  acknowledgeRefusals: (clientCaptureIds: string[]) => void;
  /** This account's photos still to send, or null when unknown. Already in `queued`. */
  photosPending: number | null;
  /** This account's photos the server refused: "Photo not sent". */
  photosRefused: PhotoRefusal[];
  /** Other accounts' photos, held with their readings. */
  photosHeld: number;
  /** A photo after the answer was queued (the walk-around). See CaptureQueue.addPhoto. */
  addPhoto: (target: string | number, photo: CapturePhoto, context?: CaptureContext) => Promise<QueuePhotoRow>;
  retryPhoto: (photoKey: string) => Promise<void>;
  discardPhoto: (photoKey: string) => Promise<void>;
}

/** What Hand over (or Sign out) needs from the dock's queue (useIirQueue). */
export interface IirDepthSource {
  pending: number | null;
  photosPending: number | null;
  refused: readonly unknown[];
  photosFailed: readonly unknown[];
}

export interface HandOverState {
  /** Both queues could be read. False = unknown, which is NOT zero (rule 15). */
  depthKnown: boolean;
  /** Everything stamped with this account still on the device: readings, inspections, photos -
   *  to send or refused. Meaningful only when depthKnown. */
  toSend: number;
  /** Signing out now strands nothing. */
  canHandOver: boolean;
}

/**
 * ⚠ ONE ANSWER TO "MAY THIS ACCOUNT LEAVE THE DEVICE?", for every control that signs out - the
 * operator shell's Hand over AND the office's Sign out (audit 28 Oct 2026: the office's skipped it,
 * so a shared tablet could be signed out with readings or inspections unsent, held for an owner
 * who had gone). Everything counts that only this account can send or discard: pending readings
 * and photos (`queued`), the line photos the server REFUSED (`photosRefused` - owner-only Send
 * again / Discard, so a handover strands them; the dock's refused photos already counted), and
 * the dock's pending and refused inspections and photos. Unknown on either side refuses.
 */
export function handOverState(
  queue: { queued: number | null; photosRefused: readonly unknown[] },
  iir: IirDepthSource,
): HandOverState {
  const iirDepth =
    iir.pending === null || iir.photosPending === null
      ? null
      : iir.pending + iir.refused.length + iir.photosPending + iir.photosFailed.length;
  const depthKnown = queue.queued !== null && iirDepth !== null;
  const toSend = (queue.queued ?? 0) + queue.photosRefused.length + (iirDepth ?? 0);
  return { depthKnown, toSend, canHandOver: depthKnown && toSend === 0 };
}

export function useQueue(options: UseQueueOptions = {}): UseQueueResult {
  const queue = options.queue ?? captureQueue();
  /** null = the store could not be read. NOT zero — see QueueSnapshot.queued.
   *  Starts at null rather than 0 so the first paint, before any snapshot has
   *  landed, does not claim an empty queue either. */
  const [queued, setQueued] = useState<number | null>(null);
  const [held, setHeld] = useState<HeldItem[]>([]);
  const [refusals, setRefusals] = useState<QueueRefusal[]>([]);
  const [refusalsDropped, setRefusalsDropped] = useState(0);
  const [photosPending, setPhotosPending] = useState<number | null>(null);
  const [photosRefused, setPhotosRefused] = useState<PhotoRefusal[]>([]);
  const [photosHeld, setPhotosHeld] = useState(0);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  // Declared BEFORE the replay effect on purpose: effects run in declaration
  // order, so identity is registered before the first replay looks for it.
  // Reversing these two would make a boot-time replay hold everything quietly
  // and then sit there until something else nudged it.
  const identity = options.identity ?? msalIdentity;
  useEffect(() => {
    setQueueIdentity(identity);
    // Deliberately never cleared on unmount. The provider reads the LIVE MSAL
    // account, so it answers '' by itself after a sign-out; clearing it when a
    // route changes would silently disarm the queue on a device that is still
    // signed in.
  }, [identity]);

  useEffect(() => {
    const apply = (snapshot: QueueSnapshot) => {
      setQueued(snapshot.queued);
      setHeld(snapshot.held);
      setRefusals(snapshot.refusals);
      setRefusalsDropped(snapshot.refusalsDropped);
      setPhotosPending(snapshot.photosPending);
      setPhotosRefused(snapshot.photosRefused);
      setPhotosHeld(snapshot.photosHeld);
    };
    const unsubscribe = queue.subscribe(apply);
    void queue.snapshot().then(apply);
    return unsubscribe;
  }, [queue]);

  // Boot, and every account change on this device. A queue that outlived a
  // handover drains the moment its own account signs back in — with no human
  // action at all, which is the whole recovery path for a held item.
  useEffect(() => {
    void queue.replay();
  }, [queue, options.signedInUpn]);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      // The link is back, so the last failure's backoff is stale: waiting out a
      // 60s timer with a working network is 60s of an operator watching a chip
      // that says "queued" for no reason.
      queue.resetBackoff();
      void queue.replay();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [queue]);

  const enqueue = useCallback(
    async (draft: CaptureDraft) => {
      // Every capture goes through the queue, online or not. The keypad must not
      // wait on a round trip to tell the operator the reading was taken, and the
      // gate that judges it is server-side anyway.
      const row = await queue.enqueue(draft);
      if (typeof navigator === 'undefined' || navigator.onLine) void queue.replay();
      return row;
    },
    [queue],
  );

  const replay = useCallback(() => queue.replay(), [queue]);

  const discardHeld = useCallback(
    (queueId: number, ownerUpn: string) => queue.discardHeld(queueId, ownerUpn),
    [queue],
  );

  const owners = new Set(held.map((item) => item.upn));
  // `|| null` and not `?? null`: an unstamped row's owner is '', and an empty
  // string rendered into "they will send when  signs in" is worse than the
  // banner's own "another account" fallback.
  const heldOwner = owners.size === 1 ? [...owners][0] || null : null;

  const acknowledgeRefusals = useCallback(
    (ids: string[]) => queue.acknowledgeRefusals(ids),
    [queue],
  );

  const addPhoto = useCallback(
    async (target: string | number, photo: CapturePhoto, context?: CaptureContext) => {
      const row = await queue.addPhoto(target, photo, context);
      if (typeof navigator === 'undefined' || navigator.onLine) void queue.replay();
      return row;
    },
    [queue],
  );
  // The owner is the account signed in NOW: the queue refuses anyone else's photo.
  const ownerNow = () => identity.getUpn();
  const retryPhoto = useCallback((photoKey: string) => queue.retryPhoto(photoKey, ownerNow()), [queue, identity]); // eslint-disable-line react-hooks/exhaustive-deps
  const discardPhoto = useCallback((photoKey: string) => queue.discardPhoto(photoKey, ownerNow()), [queue, identity]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    queued,
    held,
    heldOwner,
    online,
    enqueue,
    replay,
    discardHeld,
    refusals,
    refusalsDropped,
    acknowledgeRefusals,
    photosPending,
    photosRefused,
    photosHeld,
    addPhoto,
    retryPhoto,
    discardPhoto,
  };
}
